import * as THREE from 'three';
import { GLSL_NOISE } from './shaders/common.glsl';
import { GALACTIC_CENTRE, GALACTIC_NORMAL } from './shaders/galaxyDefaults';

/**
 * The galaxy, baked once into a cube map.
 *
 * Everything in this background is low-frequency: the Milky Way band, its dust
 * lanes, the glow of unresolved stars, a handful of nebulae. None of it moves,
 * none of it depends on where the player is — at interstellar distances nothing
 * the player can do produces parallax — and all of it is expensive to evaluate
 * per pixel. That combination is exactly what a bake is for. The procedural
 * shader runs six times at construction and never again; from then on the
 * background costs one cube map lookup.
 *
 * Resolved stars are deliberately *not* baked. Points stay crisp at any output
 * resolution, while a baked star is stuck at the texel size of the face it
 * landed on and turns into a blurry smudge the moment the player zooms or plays
 * at a higher resolution.
 */

const VERTEX = /* glsl */ `
varying vec3 vDirection;
void main() {
  vDirection = position;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAGMENT = /* glsl */ `
precision highp float;

${GLSL_NOISE}

varying vec3 vDirection;

uniform vec3 uGalacticNormal;
uniform vec3 uGalacticCentre;

const float BLOB_EPS = 1e-6; // guards the blob's size-squared denominator

/** Soft-edged blob used for nebulae, in direction space. */
float blob(vec3 dir, vec3 centre, float size) {
  float d = length(dir - normalize(centre));
  return exp(-(d * d) / max(size * size, BLOB_EPS));
}

// --- The disc: thickness/brightness in the bulge, star-cloud structure ----
const float BULGE_POWER = 3.0;        // sharpens the brightening toward galactic centre
const float DISC_THICKNESS_BASE = 0.055;  // latitude Gaussian sigma away from the bulge
const float DISC_THICKNESS_BULGE_GAIN = 0.075; // additional sigma at the bulge
const float STAR_CLOUD_FREQ_COARSE = 7.0;
const float STAR_CLOUD_FREQ_FINE = 21.0;
const float STAR_CLOUD_WEIGHT_COARSE = 0.6;
const float STAR_CLOUD_WEIGHT_FINE = 0.4;
const float STAR_GLOW_CLOUD_FLOOR = 0.45;
const float STAR_GLOW_CLOUD_GAIN = 1.1;
const float STAR_GLOW_BULGE_FLOOR = 0.30;
const float STAR_GLOW_BULGE_GAIN = 0.85;

// --- Dust lanes: dark filaments in the plane -------------------------------
const float DUST_LATITUDE_SIGMA = 0.028; // narrower than the star band itself, hence the split look
const float DUST_NOISE_FREQ = 13.0;
const vec3 DUST_NOISE_OFFSET = vec3(31.7); // decorrelates dust noise from other fields sampling the same 'dir'
const float DUST_THRESHOLD_START = 0.42;
const float DUST_THRESHOLD_END = 0.72;
const float DUST_DARKEN_STRENGTH = 0.85;

// Warm in the bulge (old, reddened stars), cooler in the outer arms.
const vec3 DISC_COLOR_WARM = vec3(1.0, 0.82, 0.58);
const vec3 DISC_COLOR_COOL = vec3(0.72, 0.80, 1.0);
const float DISC_COLOR_BULGE_GAIN = 1.3;
const float DISC_COLOR_BULGE_BIAS = 0.15;

// The 0.16 this carried was calibrated when the night side was exposed for
// daylight. Once the exposure started adapting to local darkness — a 2.9x lift
// at Halcyon's midnight — the band went from a subtle structure to a blown-out
// white smear across half the sky, which is the one thing the Milky Way never
// looks like. Rescaled by roughly the reciprocal of that lift so it reads the
// same as before against the new exposure, and so the dust lanes stay visible
// as lanes rather than being buried in a saturated wash.
const float DISC_EXPOSURE_SCALE = 0.055;

// --- Diffuse sky glow -------------------------------------------------------
// Faint everywhere: unresolved stars well off the plane, plus a trace of
// zodiacal-style haze. Deliberately near black — the night sky between the
// stars really is, and anything brighter reads as fog rather than as space.
const vec3 SKY_GLOW_COLOR = vec3(0.0022, 0.0026, 0.0042);
const float SKY_GLOW_FREQ = 3.0;
const float SKY_GLOW_FLOOR = 0.5;

// --- Nebulae -----------------------------------------------------------
// A few named-looking patches. Emission nebulae are red (hydrogen alpha),
// reflection nebulae blue; both sit in or near the plane, which is where the
// gas is. Each is (colour, centre direction, angular size, detail noise
// frequency + offset, detail floor/gain, overall strength).
const float NEBULA_STRENGTH = 0.05; // shared overall opacity multiplier for every patch below

void main() {
  vec3 dir = normalize(vDirection);

  // Galactic latitude: zero in the plane of the disc, +/-1 at the poles.
  float lat = dot(dir, normalize(uGalacticNormal));

  // --- The disc ------------------------------------------------------------
  // A thin luminous band, thickened and brightened toward the galactic centre
  // where the bulge is. The falloff is a Gaussian in latitude, which is what an
  // edge-on exponential disc actually looks like from inside it.
  float towardCentre = max(dot(dir, normalize(uGalacticCentre)), 0.0);
  float bulge = pow(towardCentre, BULGE_POWER);
  float thickness = DISC_THICKNESS_BASE + bulge * DISC_THICKNESS_BULGE_GAIN;
  float band = exp(-(lat * lat) / (2.0 * thickness * thickness));

  // Structure along the band: unresolved star clouds at two scales.
  float clouds = fbm(dir * STAR_CLOUD_FREQ_COARSE, 5) * STAR_CLOUD_WEIGHT_COARSE
                + fbm(dir * STAR_CLOUD_FREQ_FINE, 4) * STAR_CLOUD_WEIGHT_FINE;
  float starGlow = band * (STAR_GLOW_CLOUD_FLOOR + clouds * STAR_GLOW_CLOUD_GAIN)
                        * (STAR_GLOW_BULGE_FLOOR + bulge * STAR_GLOW_BULGE_GAIN);

  // --- Dust lanes ----------------------------------------------------------
  // Dark filaments lying in the plane, which is what gives the Milky Way its
  // split appearance rather than a smooth stripe.
  float duststrength = exp(-(lat * lat) / (2.0 * DUST_LATITUDE_SIGMA * DUST_LATITUDE_SIGMA));
  float dust = fbm(dir * DUST_NOISE_FREQ + DUST_NOISE_OFFSET, 5);
  float lanes = 1.0 - duststrength * smoothstep(DUST_THRESHOLD_START, DUST_THRESHOLD_END, dust) * DUST_DARKEN_STRENGTH;

  // Warm in the bulge, cooler in the outer arms: the centre is dominated by old
  // stars and reddened by the dust in front of it.
  vec3 discColour = mix(DISC_COLOR_COOL, DISC_COLOR_WARM, clamp(bulge * DISC_COLOR_BULGE_GAIN + DISC_COLOR_BULGE_BIAS, 0.0, 1.0));

  vec3 colour = discColour * starGlow * lanes * DISC_EXPOSURE_SCALE;

  // --- Diffuse sky glow ----------------------------------------------------
  colour += SKY_GLOW_COLOR * (SKY_GLOW_FLOOR + fbm(dir * SKY_GLOW_FREQ, 3));

  // --- Nebulae -------------------------------------------------------------
  colour += vec3(0.95, 0.24, 0.30) * blob(dir, vec3(0.86, 0.10, 0.50), 0.115)
          * (0.35 + fbm(dir * 26.0, 4) * 0.9) * NEBULA_STRENGTH;
  colour += vec3(0.30, 0.48, 1.00) * blob(dir, vec3(-0.62, -0.16, 0.77), 0.085)
          * (0.30 + fbm(dir * 33.0 + vec3(11.0), 4) * 0.85) * NEBULA_STRENGTH;
  colour += vec3(0.55, 0.95, 0.72) * blob(dir, vec3(-0.15, 0.42, -0.90), 0.06)
          * (0.25 + fbm(dir * 40.0 + vec3(5.0), 3) * 0.8) * NEBULA_STRENGTH;
  colour += vec3(1.00, 0.62, 0.22) * blob(dir, vec3(0.35, -0.55, -0.76), 0.05)
          * (0.30 + fbm(dir * 48.0 + vec3(23.0), 3) * 0.7) * NEBULA_STRENGTH;
  colour += vec3(0.95, 0.24, 0.30) * blob(dir, vec3(0.75, -0.65, 0.10), 0.04)
          * (0.25 + fbm(dir * 55.0 + vec3(17.0), 3) * 0.6) * NEBULA_STRENGTH;

  gl_FragColor = vec4(max(colour, vec3(0.0)), 1.0);
}
`;

export class GalaxyBackground {
  private readonly target: THREE.WebGLCubeRenderTarget;

  /**
   * Bakes the background. Runs six render passes and then never touches the
   * GPU again.
   *
   * @param faceSize texels per cube face. The content is low-frequency, so this
   *   can stay modest; 1024 is already past the point where the band's gradient
   *   shows any banding.
   */
  constructor(renderer: THREE.WebGLRenderer, faceSize = 1024) {
    this.target = new THREE.WebGLCubeRenderTarget(faceSize, {
      // Half float: the band spans a wide dynamic range and the tone mapper is
      // applied afterwards, so clamping to 8 bits here would quantise the
      // faintest parts into flat blocks.
      type: THREE.HalfFloatType,
      generateMipmaps: true,
      minFilter: THREE.LinearMipmapLinearFilter,
      magFilter: THREE.LinearFilter,
    });

    const material = new THREE.ShaderMaterial({
      uniforms: {
        // Tilted well away from the ecliptic so the band crosses the sky at an
        // angle rather than lying along the planets' orbits.
        uGalacticNormal: { value: new THREE.Vector3(...GALACTIC_NORMAL).normalize() },
        uGalacticCentre: { value: new THREE.Vector3(...GALACTIC_CENTRE).normalize() },
      },
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,
      toneMapped: false,
    });

    const scene = new THREE.Scene();
    const box = new THREE.Mesh(new THREE.BoxGeometry(2, 2, 2), material);
    scene.add(box);

    const camera = new THREE.CubeCamera(0.1, 10, this.target);
    const previousTarget = renderer.getRenderTarget();
    const previousToneMapping = renderer.toneMapping;
    // The bake stores scene-referred radiance; tone mapping happens when the
    // background is finally drawn, not when it is stored.
    renderer.toneMapping = THREE.NoToneMapping;
    camera.update(renderer, scene);
    renderer.toneMapping = previousToneMapping;
    renderer.setRenderTarget(previousTarget);

    box.geometry.dispose();
    material.dispose();
  }

  get texture(): THREE.CubeTexture {
    return this.target.texture;
  }

  dispose(): void {
    this.target.dispose();
  }
}
